import { useState } from 'react';

import { Field, controlClass } from '@/components/Field';
import { Button, Notice } from '@/components/PageHeading';
import { useEscapeKey } from '@/components/shortcuts';
import { emptyAccessory, type DraftAccessory } from '@core/bookings/draft';
import { accessoriesProblem } from '@core/bookings/garmentSheet';

/**
 * What goes out attached to one garment — the kalgi, the brooch, the dupatta (spec Section 9).
 *
 * <p>
 * <strong>Recorded here so the "missing accessories" deduction has a source.</strong> Settlement
 * charges for an accessory that was issued and did not come back; until this existed, nothing in
 * the product could create the row, so both counters rendered a permanently empty list and the
 * deduction had to be invented at the counter — which is exactly what
 * <code>ReturnCounter</code>'s own comment says it must not be.
 * </p>
 *
 * <p>
 * <strong>It takes and returns a plain list rather than editing in place</strong>, so the create
 * page and the amendment screen can share it: one holds a `DraftGarment` with no id yet and the
 * other an incoming line, and a component reaching into either would grow a union for no gain. Same
 * reasoning {@link GarmentDiscountModal} gives about itself.
 * </p>
 *
 * <p>
 * There is deliberately no "issued" control. A booking says what the shop <em>intends</em> to send;
 * only the issue counter can say what physically left, and a row created as already-issued would let
 * a booking nobody fulfilled produce a charge.
 * </p>
 */
export function GarmentAccessoriesModal({
  name,
  serialCode,
  initial,
  currency = '',
  busy = false,
  onClose,
  onApply,
}: {
  name: string;
  serialCode: string;
  initial: DraftAccessory[];
  currency?: string;
  busy?: boolean;
  onClose: () => void;
  onApply: (accessories: DraftAccessory[]) => void;
}) {
  // Seeded with one blank row when there is nothing yet, so the panel opens ready to type rather
  // than on an empty state with a button to press first.
  const [rows, setRows] = useState<DraftAccessory[]>(
    initial.length > 0 ? initial : [emptyAccessory()],
  );

  useEscapeKey(onClose);

  const update = (key: string, change: Partial<DraftAccessory>) =>
    setRows((was) => was.map((row) => (row.key === key ? { ...row, ...change } : row)));

  const named = rows.filter((row) => row.name.trim().length > 0);

  // The core's check, shared with the phone: only named rows count, and a blank one is dropped.
  const problem = accessoriesProblem(rows);

  return (
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex items-center justify-center p-4 print:hidden"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-[var(--overlay-scrim)]"
      />

      <div className="border-[var(--overlay-border)] relative flex max-h-full w-full max-w-[520px] flex-col rounded border bg-[var(--overlay-surface)] shadow-lg">
        <header className="border-border-color border-b px-4 py-3">
          <p className="text-sm font-semibold text-gray-900">What goes out with this garment</p>
          <p className="text-default truncate text-[12px]">
            {name} · <span className="font-mono-nums">{serialCode}</span>
          </p>
        </header>

        <div className="flex flex-col gap-2.5 overflow-y-auto px-4 py-3">
          <Notice tone="info" title="Ticked at the counter, not here">
            This is what the shop intends to send. The issue counter records what actually left, and
            the return counter what came back — an accessory that went out and did not return is
            deducted from the deposit.
          </Notice>

          {/* A card per accessory rather than a row of bare inputs. Three controls side by side in a
              modal this wide leaves each of them a stump, and an unlabelled stump on the screen that
              decides a deduction is how the wrong figure gets recorded — the same lesson the tailor's
              measurement rows taught. `list-none`/`ps-0` go on the elements themselves because
              style.css sets `ul li { list-disc }` as a descendant rule, which a class on the parent
              cannot beat. */}
          <ul className="flex list-none flex-col gap-2 ps-0">
            {rows.map((row) => (
              <li
                key={row.key}
                className="border-border-color list-none rounded border bg-light px-3 py-2.5 ps-3"
              >
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_5rem_7rem]">
                  <Field label="What it is">
                    <input
                      value={row.name}
                      disabled={busy}
                      placeholder="Kalgi"
                      onChange={(event) => update(row.key, { name: event.target.value })}
                      className={controlClass}
                    />
                  </Field>

                  <Field label="How many">
                    <input
                      type="number"
                      min={1}
                      step="1"
                      value={row.quantity}
                      disabled={busy}
                      onChange={(event) =>
                        update(row.key, { quantity: Math.trunc(Number(event.target.value)) || 0 })
                      }
                      // Scrolling the panel past a number input steps its value, which here would
                      // quietly change what the customer can be charged for.
                      onWheel={(event) => event.currentTarget.blur()}
                      className={controlClass}
                    />
                  </Field>

                  <Field label={`If lost (${currency || '₹'})`} hint="Blank absorbs it">
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={row.replacementCharge ?? ''}
                      disabled={busy}
                      placeholder="—"
                      onChange={(event) =>
                        update(row.key, {
                          replacementCharge:
                            event.target.value.trim() === '' ? null : Number(event.target.value),
                        })
                      }
                      onWheel={(event) => event.currentTarget.blur()}
                      className={controlClass}
                    />
                  </Field>
                </div>

                <div className="mt-1.5 flex justify-end">
                  <Button
                    variant="danger"
                    disabled={busy}
                    onClick={() => setRows((was) => was.filter((one) => one.key !== row.key))}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>

          <div>
            <Button
              disabled={busy}
              data-enter-key-commit
              onClick={() => setRows((was) => [...was, emptyAccessory()])}
            >
              Add another
            </Button>
          </div>

          {problem !== null && (
            <Notice tone="danger" title="Check the lines">
              {problem}
            </Notice>
          )}
        </div>

        <footer className="border-border-color flex items-center gap-2 border-t px-4 py-3">
          <span className="me-auto text-default text-[12px]">
            {named.length === 0
              ? 'Nothing goes out with it'
              : `${named.length} accessor${named.length === 1 ? 'y' : 'ies'}`}
          </span>

          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>

          <Button
            variant="primary"
            shortcut="save"
            disabled={problem !== null || busy}
            onClick={() => onApply(named)}
          >
            Save
          </Button>
        </footer>
      </div>
    </div>
  );
}
